'use client';

import { createContext, type ReactNode } from 'react';

/**
 * Chart chrome for the Verdict strategy boards. Off by default, so factor
 * chapters and X-Ray keep their headers unchanged. When on, ChartHeader:
 *   - shows the one-line takeaway under the title ("$26B → $31B +6% CAGR",
 *     "median 21x · Fwd 19x -10%", "1Y -20% -38pp vs Global");
 *   - drops the "Actual / Est" legend — dashed line, shaded zone and the
 *     "today" divider already mark the forecast. Series legends
 *     (EBITDA / EBIT / Net Income, stock vs benchmark) stay.
 */
export const ChartBoardContext = createContext(false);

/**
 * Per-cell chart swap: when provided around a MiniChart, its title becomes a
 * dropdown of registry charts (ChartSwapMenu). Verdict boards use it so a slot
 * can show a different chart for a specific post; elsewhere titles stay text.
 */
export type ChartSwapValue = {
  /** Registry key of the chart currently in the slot. */
  currentKey: string;
  onSelect: (key: string) => void;
  /** Present when the slot differs from its strategy default. */
  onReset?: () => void;
};

export const ChartSwapContext = createContext<ChartSwapValue | null>(null);

/**
 * Optional title renderer for plain (non-swappable) MiniChart titles — the
 * Heatmap Charts grid uses it to set label emoji smaller than the name.
 */
export const ChartTitleRenderContext = createContext<((title: string) => ReactNode) | null>(null);

/**
 * Optional box in the header's top-right corner (Heatmap Charts grid: the
 * value the grid is sorted by). When set, series legends move under the title.
 */
export const ChartCornerContext = createContext<ReactNode>(null);

/**
 * Weekly closes (stock + ACWI benchmark) for the "52 Week Return" chart. It is
 * the one registry chart not built from company_historicals, so MiniChart reads
 * the series from here. Provided by the Verdict board and the Financials tab;
 * without it the chart shows its empty state (Heatmap hides it from the picker).
 */
export type WeeklyPrice = { week_date: string; price_close: number };
export type WeeklyPricesValue = { weeklyPrices: WeeklyPrice[]; benchmarkPrices: WeeklyPrice[] };

export const WeeklyPricesContext = createContext<WeeklyPricesValue | null>(null);
